import { Building2, Clock3, PhoneIncoming, PhoneOutgoing, Plus, Search, Sparkles, Trash2, Upload, Users, X, CheckCircle, AlertCircle, SkipForward, Calendar, Tag } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { SurfaceCard } from '../../components/ui/SurfaceCard';
import { crmApi, type ApiRecord } from '../../lib/api';
import { ClickToCallButton } from '../phone/Softphone';

const SINGULAR: Record<string, string> = {
	Companies: 'Company',
	Contacts: 'Contact',
	Activities: 'Activity',
	Opportunities: 'Opportunity',
	Leads: 'Lead'
};
const singular = (t: string) => SINGULAR[t] ?? t.replace(/s$/, '');

const RESOURCE_META = {
	companies: { Icon: Building2, tone: 'text-sky-600 dark:text-sky-400', blurb: 'Accounts you sell into. Each company can have many contacts; a contact with a lead status is a Lead.' },
	contacts: { Icon: Users, tone: 'text-violet-600 dark:text-violet-400', blurb: 'People at your companies. Promote any contact to a Lead to work them through the pipeline.' },
	opportunities: { Icon: Sparkles, tone: 'text-amber-600 dark:text-amber-400', blurb: 'Active deals tied to a company and contact.' },
	activities: { Icon: Clock3, tone: 'text-emerald-600 dark:text-emerald-400', blurb: 'Notes and touchpoints across your deals.' }
} as const;

const LEAD_STATUS_OPTIONS = [
	{ value: 'new',            label: 'New',            color: 'bg-surface-hover text-secondary border-subtle' },
	{ value: 'attempted',      label: 'Attempted',      color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
	{ value: 'contacted',      label: 'Contacted',      color: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30' },
	{ value: 'interested',     label: 'Interested',     color: 'bg-accent/15 text-accent border-accent/40' },
	{ value: 'follow_up',      label: 'Follow Up',      color: 'bg-warning/15 text-orange-700 dark:text-warning border-warning/30' },
	{ value: 'future',         label: 'Future',         color: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30' },
	{ value: 'not_interested', label: 'Not Interested', color: 'bg-danger/15 text-danger border-danger/30' },
	{ value: 'converted',      label: 'Converted',      color: 'bg-success/15 text-success border-success/30' },
] as const;

const LEAD_SOURCE_OPTIONS = [
	{ value: '', label: 'No source' },
	{ value: 'prospector', label: 'Prospector' },
	{ value: 'lead_gen', label: 'Lead Gen' },
	{ value: 'list', label: 'List' },
	{ value: 'referral', label: 'Referral' },
	{ value: 'inbound', label: 'Inbound' },
	{ value: 'manual', label: 'Manual' },
];

function LeadStatusBadge({ status }: { status: string }) {
	const opt = LEAD_STATUS_OPTIONS.find((o) => o.value === status);
	if (!opt) return null;
	return (
		<span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs font-medium ${opt.color}`}>
			{opt.label}
		</span>
	);
}

const listApiMap = {
	companies: crmApi.companies,
	contacts: crmApi.contacts,
	opportunities: crmApi.opportunities,
	activities: crmApi.opportunities,
	emails: crmApi.emails
};

const createApiMap = {
	companies: crmApi.createCompany,
	contacts: crmApi.createContact,
	opportunities: crmApi.createOpportunity
};

const deleteApiMap = {
	companies: crmApi.deleteCompany,
	contacts: crmApi.deleteContact,
	opportunities: crmApi.deleteOpportunity
};

type Resource = keyof typeof listApiMap;

function formatPrimaryValue(record: ApiRecord, resource: Resource, fallbackIndex: number) {
	if (resource === 'contacts') {
		const first = String(record.first_name ?? '').trim();
		const last = String(record.last_name ?? '').trim();
		const full = `${first} ${last}`.trim();
		return full || String(record.email ?? `Record ${fallbackIndex + 1}`);
	}
	return String(record.name ?? record.title ?? record.subject ?? record.email ?? `Record ${fallbackIndex + 1}`);
}

function formatSecondaryValue(record: ApiRecord, resource: Resource) {
	if (resource === 'companies') {
		return String(record.website_domain ?? 'Company');
	}
	if (resource === 'contacts') {
		const parts = [record.title, record.email].filter(Boolean);
		return String(parts.join(' · ') || 'Contact');
	}
	if (resource === 'opportunities') {
		return `Stage: ${String(record.stage ?? 'Opportunity')} · Vendor: ${String((record.vendor as ApiRecord | undefined)?.name ?? 'Unassigned')} · Owner: ${String((record.owner as ApiRecord | undefined)?.name ?? 'Unassigned')}`;
	}
	if (resource === 'activities') {
		return `Vendor: ${String((record.vendor as ApiRecord | undefined)?.name ?? 'Unassigned')} · Owner: ${String((record.owner as ApiRecord | undefined)?.name ?? 'Unassigned')}`;
	}
	return String(record.type ?? record.stage ?? record.status ?? resource);
}

function FollowUpPanel({ initialdDate, initialNote, onSave }: {
	initialdDate: string;
	initialNote: string;
	onSave: (date: string, note: string) => void;
}) {
	const [date, setDate] = useState(initialdDate);
	const [note, setNote] = useState(initialNote);

	return (
		<div className="space-y-1">
			<h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Schedule follow-up</h3>
			<input
				type="date"
				value={date}
				onChange={(e) => setDate(e.target.value)}
				className="w-full rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary"
			/>
			<textarea
				value={note}
				onChange={(e) => setNote(e.target.value)}
				placeholder="Follow-up note..."
				className="min-h-16 w-full rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary"
			/>
			<button
				onClick={() => onSave(date, note)}
				className="rounded-xl bg-orange-500/80 px-2 py-1.5 text-xs font-semibold text-primary hover:bg-orange-500"
			>
				<Calendar className="mr-1 inline h-3 w-3" />
				{date ? 'Save follow-up' : 'Clear follow-up'}
			</button>
		</div>
	);
}

export function CollectionPage({
	title,
	resource
}: {
	title: string;
	resource: Resource;
}) {
	const queryClient = useQueryClient();
	const [query, setQuery] = useState('');
	const [createOpen, setCreateOpen] = useState(false);
	const [importOpen, setImportOpen] = useState(false);
	const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: number; results: ApiRecord[] } | null>(null);
	const importFileRef = useRef<HTMLInputElement>(null);
	const [selectedContactId, setSelectedContactId] = useState<string>('');
	const [activityForm, setActivityForm] = useState<Record<string, string>>({
		opportunity_id: '',
		subject: '',
		body: ''
	});
	const [leadStatusFilter, setLeadStatusFilter] = useState<string>('');
	const [form, setForm] = useState<Record<string, string>>({
		name: '',
		website_domain: '',
		first_name: '',
		last_name: '',
		email: '',
		phone: '',
		title: '',
		company_id: '',
		contact_id: '',
		stage: 'New',
		lead_status: 'new',
		lead_source: ''
	});

	const result = useQuery({
		queryKey: [resource, query, leadStatusFilter],
		queryFn: () => {
			const params: Record<string, string> = {};
			if (query) params.q = query;
			if (resource === 'contacts' && leadStatusFilter) params.lead_status = leadStatusFilter;
			return listApiMap[resource](Object.keys(params).length ? params : undefined);
		}
	});

	const companiesQuery = useQuery({
		queryKey: ['companies', 'options'],
		queryFn: () => crmApi.companies(),
		enabled: resource === 'contacts' || resource === 'opportunities'
	});

	const contactsQuery = useQuery({
		queryKey: ['contacts', 'options'],
		queryFn: () => crmApi.contacts(),
		enabled: resource === 'opportunities'
	});

	const contactOpportunitiesQuery = useQuery({
		queryKey: ['contact-opportunities', selectedContactId],
		queryFn: () => crmApi.opportunities({ contact_id: selectedContactId }),
		enabled: resource === 'contacts' && Boolean(selectedContactId)
	});

	const selectedOpportunityId = activityForm.opportunity_id || String(contactOpportunitiesQuery.data?.[0]?.id ?? '');

	const opportunityActivitiesQuery = useQuery({
		queryKey: ['opportunity-activities', selectedOpportunityId],
		queryFn: () => crmApi.opportunityActivities(selectedOpportunityId),
		enabled: Boolean(selectedOpportunityId)
	});

	const contactCallsQuery = useQuery({
		queryKey: ['contact-calls', selectedContactId],
		queryFn: () => crmApi.contactCalls(selectedContactId),
		enabled: resource === 'contacts' && Boolean(selectedContactId)
	});

	const updateLeadStatusMutation = useMutation({
		mutationFn: async ({ id, lead_status }: { id: string; lead_status: string }) =>
			crmApi.updateContact(id, { lead_status }),
		onSuccess: async () => {
			toast.success('Lead status updated');
			await queryClient.invalidateQueries({ queryKey: ['contacts'] });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : 'Failed to update lead status');
		}
	});

	const updateFollowUpMutation = useMutation({
		mutationFn: async ({ id, follow_up_date, follow_up_note }: { id: string; follow_up_date: string; follow_up_note: string }) =>
			crmApi.updateContact(id, {
				follow_up_date: follow_up_date || null,
				follow_up_note: follow_up_note || null,
				...(follow_up_date ? { lead_status: 'follow_up' } : {})
			}),
		onSuccess: async () => {
			toast.success('Follow-up scheduled');
			await queryClient.invalidateQueries({ queryKey: ['contacts'] });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : 'Failed to schedule follow-up');
		}
	});

	const createMutation = useMutation({
		mutationFn: async () => {
			if (resource === 'companies') {
				return createApiMap.companies({
					name: form.name.trim(),
					website_domain: form.website_domain.trim() || null
				});
			}

			if (resource === 'contacts') {
				return createApiMap.contacts({
					first_name: form.first_name.trim(),
					last_name: form.last_name.trim(),
					email: form.email.trim(),
					phone: form.phone.trim() || null,
					title: form.title.trim() || null,
					company_id: form.company_id ? Number(form.company_id) : null,
					lead_status: form.lead_status || 'new',
					lead_source: form.lead_source || null
				});
			}

			return createApiMap.opportunities({
				title: form.title.trim(),
				stage: form.stage || 'New',
				company_id: Number(form.company_id),
				contact_id: form.contact_id ? Number(form.contact_id) : null
			});
		},
		onSuccess: async () => {
			toast.success(`${title.slice(0, -1)} created`);
			setCreateOpen(false);
			setForm({
				name: '',
				website_domain: '',
				first_name: '',
				last_name: '',
				email: '',
				phone: '',
				title: '',
				company_id: '',
				contact_id: '',
				stage: 'New',
				lead_status: 'new',
				lead_source: ''
			});
			await queryClient.invalidateQueries({ queryKey: [resource] });
			await queryClient.invalidateQueries({ queryKey: ['companies'] });
			await queryClient.invalidateQueries({ queryKey: ['contacts'] });
			await queryClient.invalidateQueries({ queryKey: ['opportunities'] });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : `Failed to create ${title.toLowerCase()}`);
		}
	});

	const deleteMutation = useMutation({
		mutationFn: async (id: number | string) => {
			if (resource === 'companies') {
				return deleteApiMap.companies(id);
			}
			if (resource === 'contacts') {
				return deleteApiMap.contacts(id);
			}
			return deleteApiMap.opportunities(id);
		},
		onSuccess: async () => {
			toast.success(`${title.slice(0, -1)} deleted`);
			await queryClient.invalidateQueries({ queryKey: [resource] });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : `Failed to delete ${title.toLowerCase()}`);
		}
	});

	const importMutation = useMutation({
		mutationFn: async (file: File) => crmApi.importLeads(file),
		onSuccess: async (data) => {
			setImportResult(data);
			if (importFileRef.current) {
				importFileRef.current.value = '';
			}
			if (data.imported > 0) {
				await queryClient.invalidateQueries({ queryKey: ['contacts'] });
				await queryClient.invalidateQueries({ queryKey: ['companies'] });
			}
			toast.success(`Import complete: ${data.imported} imported, ${data.skipped} skipped, ${data.errors} errors`);
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : 'Import failed');
		}
	});

	const activityMutation = useMutation({
		mutationFn: async () =>
			crmApi.createOpportunityActivity(activityForm.opportunity_id, {
				type: 'note',
				subject: activityForm.subject.trim(),
				body: activityForm.body.trim() || null
			}),
		onSuccess: async () => {
			toast.success('Activity logged');
			setActivityForm((current) => ({ ...current, subject: '', body: '' }));
			await queryClient.invalidateQueries({ queryKey: ['opportunity-activities', activityForm.opportunity_id] });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : 'Failed to create activity');
		}
	});

	const rows = useMemo(() => (Array.isArray(result.data) ? result.data.slice(0, 25) : []), [result.data]);
	const canCreate = resource === 'companies' || resource === 'contacts' || resource === 'opportunities' || resource === 'activities';
	const canDelete = resource === 'companies' || resource === 'contacts' || resource === 'opportunities';
	const companyOptions = Array.isArray(companiesQuery.data) ? companiesQuery.data : [];
	const contactOptions = Array.isArray(contactsQuery.data) ? contactsQuery.data : [];
	const relatedOpportunities = Array.isArray(contactOpportunitiesQuery.data) ? contactOpportunitiesQuery.data : [];
	const relatedActivities = Array.isArray(opportunityActivitiesQuery.data) ? opportunityActivitiesQuery.data : [];
	const contactCalls = Array.isArray(contactCallsQuery.data) ? contactCallsQuery.data : [];
	const activityOpportunities = resource === 'activities' ? rows : relatedOpportunities;

	const meta = (RESOURCE_META as Record<string, { Icon: typeof Building2; tone: string; blurb: string }>)[resource];
	const Icon = meta?.Icon ?? Building2;
	const singularTitle = singular(title);
	const totalCount = Array.isArray(result.data) ? result.data.length : 0;

	return (
		<div className="space-y-4">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex items-start gap-3">
					<div className={`flex h-10 w-10 items-center justify-center rounded-lg border border-subtle bg-surface ${meta?.tone ?? ''}`}>
						<Icon className="h-5 w-5" />
					</div>
					<div>
						<h1 className="text-xl font-semibold tracking-tight text-primary">{title}</h1>
						<p className="mt-0.5 max-w-2xl text-sm text-muted">{meta?.blurb}</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					{resource === 'contacts' ? (
						<button onClick={() => { setImportOpen((v) => !v); setImportResult(null); }} className="btn-secondary">
							<Upload className="h-3.5 w-3.5" />
							{importOpen ? 'Close import' : 'Import CSV'}
						</button>
					) : null}
					{canCreate ? (
						<button onClick={() => setCreateOpen((v) => !v)} className="btn-primary">
							<Plus className="h-3.5 w-3.5" />
							{createOpen ? 'Close' : resource === 'activities' ? 'Log activity' : `New ${singularTitle}`}
						</button>
					) : null}
				</div>
			</header>
			<SurfaceCard
				title={`${totalCount} ${totalCount === 1 ? singularTitle.toLowerCase() : title.toLowerCase()}`}
			>
				<div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex flex-1 flex-wrap items-center gap-2">
					<div className="flex w-full max-w-sm items-center gap-2 rounded-xl border border-subtle bg-surface-2 px-3 py-1.5 text-muted">
						<Search className="h-3.5 w-3.5" />
						<input
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder={`Search ${title.toLowerCase()}...`}
							className="w-full border-none bg-transparent p-0 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-0"
						/>
					</div>
					{resource === 'contacts' ? (
						<select
							value={leadStatusFilter}
							onChange={(e) => setLeadStatusFilter(e.target.value)}
							className="rounded-xl border border-subtle bg-surface-2 px-3 py-1.5 text-sm text-primary"
						>
							<option value="">All statuses</option>
							{LEAD_STATUS_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>{opt.label}</option>
							))}
						</select>
					) : null}
					</div>
				</div>

				{importOpen && resource === 'contacts' ? (
					<div className="mb-3 rounded-xl border border-subtle bg-surface-2 p-4 space-y-3">
						<div>
							<p className="text-sm font-medium text-primary mb-1">Import leads from CSV</p>
							<p className="text-xs text-muted">Expected columns: <code className="rounded bg-surface-hover px-1 py-0.5">business_name, first_name, last_name, email, phone, title, website</code></p>
							<p className="text-xs text-muted mt-0.5">Header row required. Contacts are skipped if email already exists. Companies are created or matched by name.</p>
						</div>
						<div className="flex items-center gap-3">
							<input
								ref={importFileRef}
								type="file"
								accept=".csv,text/csv"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) {
										setImportResult(null);
										importMutation.mutate(file);
									}
								}}
								className="text-sm text-secondary file:mr-3 file:rounded-xl file:border-0 file:bg-accent file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-accent-contrast file:cursor-pointer hover:file:opacity-90"
							/>
							{importMutation.isPending ? (
								<span className="text-xs text-muted animate-pulse">Importing…</span>
							) : null}
						</div>
						{importResult ? (
							<div className="space-y-2">
								<div className="flex gap-4 text-sm">
									<span className="flex items-center gap-1 text-success"><CheckCircle className="h-3.5 w-3.5" />{importResult.imported} imported</span>
									<span className="flex items-center gap-1 text-muted"><SkipForward className="h-3.5 w-3.5" />{importResult.skipped} skipped</span>
									{importResult.errors > 0 ? <span className="flex items-center gap-1 text-danger"><AlertCircle className="h-3.5 w-3.5" />{importResult.errors} errors</span> : null}
								</div>
								{importResult.results.filter((r) => String(r.status) !== 'imported').length > 0 ? (
									<div className="max-h-40 overflow-y-auto rounded-xl border border-subtle bg-canvas/60 divide-y divide-subtle">
										{importResult.results.filter((r) => String(r.status) !== 'imported').map((r, i) => (
											<div key={String(r.row ?? i)} className="flex items-start gap-2 px-3 py-1.5 text-xs">
												{String(r.status) === 'error' ? <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-danger" /> : <X className="mt-0.5 h-3 w-3 shrink-0 text-muted" />}
												<span className="text-muted">Row {String(r.row)}{r.name ? ` · ${String(r.name)}` : ''}</span>
												{r.message ? <span className="text-muted">{String(r.message)}</span> : null}
											</div>
										))}
									</div>
								) : null}
							</div>
						) : null}
					</div>
				) : null}

				{createOpen ? (
					<div className="mb-3 rounded-xl border border-subtle bg-surface-2 p-3">
						<div className="grid gap-2 md:grid-cols-2">
							{resource === 'companies' ? (
								<>
									<input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Company name" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
									<input value={form.website_domain} onChange={(event) => setForm((current) => ({ ...current, website_domain: event.target.value }))} placeholder="Website domain" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
								</>
							) : null}

							{resource === 'contacts' ? (
								<>
									<input value={form.first_name} onChange={(event) => setForm((current) => ({ ...current, first_name: event.target.value }))} placeholder="First name" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
									<input value={form.last_name} onChange={(event) => setForm((current) => ({ ...current, last_name: event.target.value }))} placeholder="Last name" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
									<input value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} placeholder="Email" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
									<input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} placeholder="Phone" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
									<input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="Title" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
									<select value={form.company_id} onChange={(event) => setForm((current) => ({ ...current, company_id: event.target.value }))} className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary">
										<option value="">No company</option>
										{companyOptions.map((company) => (
											<option key={String(company.id)} value={String(company.id)}>
												{String(company.name ?? company.id)}
											</option>
										))}
									</select>
									<select value={form.lead_status} onChange={(event) => setForm((current) => ({ ...current, lead_status: event.target.value }))} className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary">
										{LEAD_STATUS_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</select>
									<select value={form.lead_source} onChange={(event) => setForm((current) => ({ ...current, lead_source: event.target.value }))} className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary">
										{LEAD_SOURCE_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</select>
								</>
							) : null}

							{resource === 'opportunities' ? (
								<>
									<input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="Opportunity title" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
									<select value={form.company_id} onChange={(event) => setForm((current) => ({ ...current, company_id: event.target.value }))} className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary">
										<option value="">Select company</option>
										{companyOptions.map((company) => (
											<option key={String(company.id)} value={String(company.id)}>
												{String(company.name ?? company.id)}
											</option>
										))}
									</select>
									<select value={form.contact_id} onChange={(event) => setForm((current) => ({ ...current, contact_id: event.target.value }))} className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary">
										<option value="">No contact</option>
										{contactOptions.map((contact) => (
											<option key={String(contact.id)} value={String(contact.id)}>
												{String(`${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() || contact.email || contact.id)}
											</option>
										))}
									</select>
									<select value={form.stage} onChange={(event) => setForm((current) => ({ ...current, stage: event.target.value }))} className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary">
										{['New', 'Qualified', 'Proposal', 'Won', 'Lost'].map((stage) => (
											<option key={stage} value={stage}>
												{stage}
											</option>
										))}
									</select>
								</>
							) : null}

							{resource === 'activities' ? (
								<>
									<select value={activityForm.opportunity_id} onChange={(event) => setActivityForm((current) => ({ ...current, opportunity_id: event.target.value }))} className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary md:col-span-2">
										<option value="">Select opportunity</option>
										{activityOpportunities.map((opportunity) => (
											<option key={String(opportunity.id)} value={String(opportunity.id)}>
												{String(opportunity.title ?? opportunity.id)}
											</option>
										))}
									</select>
									<input value={activityForm.subject} onChange={(event) => setActivityForm((current) => ({ ...current, subject: event.target.value }))} placeholder="Activity subject" className="rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary md:col-span-2" />
									<textarea value={activityForm.body} onChange={(event) => setActivityForm((current) => ({ ...current, body: event.target.value }))} placeholder="Notes" className="min-h-20 rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary md:col-span-2" />
								</>
							) : null}
						</div>
						<div className="mt-2 flex justify-end">
							<button
								onClick={() => {
									if (resource === 'activities') {
										if (!activityForm.opportunity_id || !activityForm.subject.trim()) {
											toast.error('Opportunity and subject are required');
											return;
										}
										activityMutation.mutate();
										return;
									}
									if (resource === 'companies' && !form.name.trim()) {
										toast.error('Company name is required');
										return;
									}
									if (resource === 'contacts' && (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim())) {
										toast.error('First name, last name, and email are required');
										return;
									}
									if (resource === 'opportunities' && (!form.title.trim() || !form.company_id)) {
										toast.error('Opportunity title and company are required');
										return;
									}
									createMutation.mutate();
								}}
								className="rounded-xl bg-accent px-3 py-1.5 text-sm font-semibold text-accent-contrast"
							>
								{resource === 'activities' ? (activityMutation.isPending ? 'Saving...' : 'Save note') : createMutation.isPending ? 'Saving...' : 'Save'}
							</button>
						</div>
					</div>
				) : null}

				<div className={resource === 'contacts' ? 'grid gap-3 xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,1fr)]' : ''}>
					<div className="overflow-hidden rounded-lg border border-subtle">
						<table className="min-w-full divide-y divide-subtle text-sm">
							<thead className="bg-surface-2 text-left text-muted">
								<tr>
									<th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider">{singularTitle}</th>
									<th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider">Details</th>
									<th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider">Updated</th>
									{canDelete ? <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-right">Actions</th> : null}
								</tr>
							</thead>
							<tbody className="divide-y divide-subtle">
								{result.isLoading ? (
									<tr>
										<td colSpan={canDelete ? 4 : 3} className="px-2 py-4 text-center text-sm text-muted">
											Loading {title.toLowerCase()}...
										</td>
									</tr>
								) : rows.length > 0 ? (
									rows.map((row, index) => {
										const record = row as ApiRecord;
										const isLead = resource === 'contacts' && Boolean(record.lead_status);
										return (
											<tr key={String(record.id ?? index)} className="transition hover:bg-surface-hover">
												<td className="px-3 py-2 text-sm text-primary">
													<div className="flex items-center gap-2">
														{resource === 'companies' ? <Building2 className="h-3.5 w-3.5 shrink-0 text-sky-600 dark:text-sky-400" /> : null}
														{resource === 'contacts' ? (isLead ? <Sparkles className="h-3.5 w-3.5 shrink-0 text-accent" /> : <Users className="h-3.5 w-3.5 shrink-0 text-muted" />) : null}
														<span className="truncate font-medium">{formatPrimaryValue(record, resource, index)}</span>
														{resource === 'contacts' ? (
															<span className={`chip ${isLead ? 'border-accent/40 bg-accent/10 text-accent' : 'border-subtle bg-surface-2 text-muted'}`}>
																{isLead ? 'Lead' : 'Contact'}
															</span>
														) : null}
													</div>
												</td>
												<td className="px-3 py-2 text-sm text-secondary">
													{resource === 'contacts' && record.lead_status ? (
														<div className="flex flex-col gap-0.5">
															<span>{formatSecondaryValue(record, resource)}</span>
															<div className="flex items-center gap-1">
																<LeadStatusBadge status={String(record.lead_status)} />
																{record.follow_up_date ? (
																	<span className="inline-flex items-center gap-1 text-xs text-warning">
																		<Calendar className="h-3 w-3" />
																		{new Date(String(record.follow_up_date)).toLocaleDateString()}
																	</span>
																) : null}
																{record.lead_source ? (
																	<span className="inline-flex items-center gap-1 text-xs text-muted">
																		<Tag className="h-3 w-3" />
																		{String(record.lead_source)}
																	</span>
																) : null}
															</div>
														</div>
													) : (
														formatSecondaryValue(record, resource)
													)}
												</td>
												<td className="px-3 py-2 text-xs text-muted">{String(record.updated_at ?? record.created_at ?? 'Recently')}</td>
												{canDelete ? (
													<td className="px-3 py-2 text-right">
														{resource === 'contacts' ? (
															<>
																{record.phone ? (
																	<span className="mr-2 inline-block">
																		<ClickToCallButton
																			number={String(record.phone)}
																			contactId={record.id != null ? Number(record.id) : undefined}
																			contactName={formatPrimaryValue(record, resource, 0)}
																		/>
																	</span>
																) : null}
																<button
																	onClick={() => {
																		setSelectedContactId(String(record.id ?? ''));
																		setActivityForm({ opportunity_id: '', subject: '', body: '' });
																	}}
																	className="mr-1 inline-flex items-center gap-1.5 rounded-md border border-subtle bg-surface px-2 py-1 text-xs font-medium text-secondary transition hover:bg-surface-hover hover:text-primary"
																>
																	<Clock3 className="h-3 w-3" />
																	Timeline
																</button>
																{!isLead ? (
																	<button
																		onClick={() => {
																			if (record.id == null) return;
																			updateLeadStatusMutation.mutate({ id: String(record.id), lead_status: 'new' });
																		}}
																		className="mr-1 inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/10 px-2 py-1 text-xs font-medium text-accent transition hover:bg-accent/15"
																		title="Promote this contact to a Lead"
																	>
																		<Sparkles className="h-3 w-3" />
																		Promote
																	</button>
																) : null}
															</>
														) : null}
														<button
															onClick={() => {
																if (record.id == null) return;
																if (!confirm(`Delete this ${singular(title).toLowerCase()}?`)) return;
																deleteMutation.mutate(String(record.id));
															}}
															className="inline-flex items-center gap-1.5 rounded-md border border-subtle bg-surface px-2 py-1 text-xs font-medium text-muted transition hover:border-danger/40 hover:bg-danger/10 hover:text-danger"
														>
															<Trash2 className="h-3 w-3" />
														</button>
													</td>
												) : null}
											</tr>
										);
									})
								) : (
									<tr>
										<td colSpan={canDelete ? 4 : 3} className="px-3 py-12 text-center">
											<div className="mx-auto flex max-w-sm flex-col items-center gap-3">
												<div className={`flex h-10 w-10 items-center justify-center rounded-lg border border-subtle bg-surface ${meta?.tone ?? ''}`}>
													<Icon className="h-5 w-5" />
												</div>
												<div>
													<p className="text-sm font-medium text-primary">No {title.toLowerCase()} yet</p>
													<p className="mt-0.5 text-xs text-muted">{query ? 'Try adjusting your search.' : `Create your first ${singularTitle.toLowerCase()} to get started.`}</p>
												</div>
												{canCreate && !query ? (
													<button onClick={() => setCreateOpen(true)} className="btn-primary">
														<Plus className="h-3.5 w-3.5" /> New {singularTitle}
													</button>
												) : null}
											</div>
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					{resource === 'contacts' ? (
						<SurfaceCard title="Contact timeline">
							{selectedContactId ? (
								<div className="space-y-2">
									{(() => {
										const selectedContact = rows.find((r) => String((r as ApiRecord).id) === selectedContactId) as ApiRecord | undefined;
										if (!selectedContact) return null;
										return (
											<div className="space-y-2 rounded-xl border border-subtle bg-surface-2 p-3">
												<div className="flex items-center justify-between">
													<h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Lead status</h3>
													<LeadStatusBadge status={String(selectedContact.lead_status ?? 'new')} />
												</div>
												<select
													defaultValue={String(selectedContact.lead_status ?? 'new')}
													key={selectedContactId + String(selectedContact.lead_status)}
													onChange={(e) => updateLeadStatusMutation.mutate({ id: selectedContactId, lead_status: e.target.value })}
													className="w-full rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary"
												>
													{LEAD_STATUS_OPTIONS.map((opt) => (
														<option key={opt.value} value={opt.value}>{opt.label}</option>
													))}
												</select>
												<FollowUpPanel
													initialdDate={selectedContact.follow_up_date ? String(selectedContact.follow_up_date).slice(0, 10) : ''}
													initialNote={String(selectedContact.follow_up_note ?? '')}
													onSave={(date, note) => updateFollowUpMutation.mutate({ id: selectedContactId, follow_up_date: date, follow_up_note: note })}
												/>
											</div>
										);
									})()}
									<div className="space-y-1">
										<h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Related opportunities</h3>
										{relatedOpportunities.length > 0 ? (
											relatedOpportunities.map((opportunity) => (
												<button
													key={String(opportunity.id)}
													onClick={() => setActivityForm((current) => ({ ...current, opportunity_id: String(opportunity.id ?? '') }))}
													className={`w-full rounded-xl border px-2 py-1.5 text-left ${String(opportunity.id) === activityForm.opportunity_id ? 'border-accent/50 bg-accent/10' : 'border-subtle bg-surface-2'}`}
												>
													<p className="font-medium text-primary">{String(opportunity.title ?? `Opportunity ${String(opportunity.id)}`)}</p>
													<p className="mt-1 text-xs text-muted">
														{String(opportunity.stage ?? 'Stage')} · Vendor: {String((opportunity.vendor as ApiRecord | undefined)?.name ?? 'Unassigned')} · Owner: {String((opportunity.owner as ApiRecord | undefined)?.name ?? 'Unassigned')}
													</p>
												</button>
											))
										) : (
											<div className="rounded-2xl border border-subtle bg-surface-2 p-3 text-sm text-muted">No related opportunities yet.</div>
										)}
									</div>
									<div className="space-y-1">
										<h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Log note</h3>
										<select value={activityForm.opportunity_id} onChange={(event) => setActivityForm((current) => ({ ...current, opportunity_id: event.target.value }))} className="w-full rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary">
											<option value="">Select opportunity</option>
											{relatedOpportunities.map((opportunity) => (
												<option key={String(opportunity.id)} value={String(opportunity.id)}>
													{String(opportunity.title ?? opportunity.id)}
												</option>
											))}
										</select>
										<input value={activityForm.subject} onChange={(event) => setActivityForm((current) => ({ ...current, subject: event.target.value }))} placeholder="What happened?" className="w-full rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
										<textarea value={activityForm.body} onChange={(event) => setActivityForm((current) => ({ ...current, body: event.target.value }))} placeholder="Detailed note, product pitched, objections, next step..." className="min-h-20 w-full rounded-xl border border-subtle bg-canvas px-2 py-1.5 text-sm text-primary" />
										<button
											onClick={() => {
												if (!activityForm.opportunity_id || !activityForm.subject.trim()) {
													toast.error('Opportunity and subject are required');
													return;
												}
												activityMutation.mutate();
											}}
											className="rounded-xl bg-accent px-2 py-1.5 text-sm font-semibold text-accent-contrast"
										>
											{activityMutation.isPending ? 'Saving...' : 'Add note'}
										</button>
									</div>
									<div className="space-y-1">
										<h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Timeline</h3>
										{relatedActivities.length > 0 ? (
											relatedActivities.map((activity) => (
												<div key={String(activity.id)} className="rounded-xl border border-subtle bg-surface-2 p-2">
													<p className="text-sm font-medium text-primary">{String(activity.subject ?? 'Activity')}</p>
													<p className="text-xs text-muted">By {String((activity.user as ApiRecord | undefined)?.name ?? 'Unknown')} · {String(activity.at ?? activity.created_at ?? '')}</p>
													{activity.body ? <p className="mt-1.5 text-xs leading-5 text-secondary">{String(activity.body)}</p> : null}
												</div>
											))
										) : (
											<div className="rounded-2xl border border-subtle bg-surface-2 p-3 text-sm text-muted">No notes logged yet for the selected opportunity.</div>
										)}
									</div>
									<div className="space-y-1">
										<h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Call history</h3>
										{contactCalls.length > 0 ? (
											contactCalls.map((call) => {
												const isInbound = String(call.direction) === 'inbound';
												const number = isInbound ? String(call.from_number ?? '') : String(call.to_number ?? '');
												const duration = Number(call.duration_seconds ?? 0);
												const durationStr = duration > 0 ? `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}` : '0:00';
												const statusColor =
													String(call.status) === 'completed' ? 'text-success'
														: String(call.status) === 'failed' || String(call.status) === 'busy' || String(call.status) === 'no_answer' ? 'text-danger'
															: 'text-muted';

												return (
													<div key={String(call.id)} className="flex items-start gap-2 rounded-xl border border-subtle bg-surface-2 p-2">
														<div className="mt-0.5">
															{isInbound
																? <PhoneIncoming className="h-4 w-4 text-blue-400" />
																: <PhoneOutgoing className="h-4 w-4 text-accent" />
															}
														</div>
														<div className="min-w-0 flex-1">
															<div className="flex items-center justify-between">
																<p className="font-medium text-primary">
																	{isInbound ? 'Inbound' : 'Outbound'} · {number || 'Unknown'}
																</p>
																<span className="text-xs tabular-nums text-muted">{durationStr}</span>
															</div>
															<p className="mt-1 text-xs text-muted">
																<span className={statusColor}>{String(call.status ?? 'unknown')}</span>
																{call.disposition ? ` · ${String(call.disposition)}` : ''}
																{' · '}
																{String(call.started_at ?? call.created_at ?? '')}
															</p>
															{call.notes ? <p className="mt-2 text-sm leading-6 text-secondary">{String(call.notes)}</p> : null}
														</div>
													</div>
												);
											})
										) : (
											<div className="rounded-2xl border border-subtle bg-surface-2 p-4 text-sm text-muted">No call records for this contact.</div>
										)}
									</div>
								</div>
							) : (
								<div className="rounded-2xl border border-subtle bg-surface-2 p-4 text-sm text-muted">Select a contact from the table to view timeline and related product / owner context.</div>
							)}
						</SurfaceCard>
					) : null}
				</div>
			</SurfaceCard>
		</div>
	);
}
